import styled from 'styled-components';

import { EditIcon } from '@genially/icons';

import type { Color, Size as ThemeSize } from '../../../style/types/theme';
import { Size } from '../types';
import { Image } from '../Common.styled';

type WrapperProps = {
  $size: Size;
  $editable: boolean;
};

type TextWrapperStyledProps = {
  $size: Size;
};

const getSize = (size: Size, themeSize: ThemeSize) => {
  switch (size) {
    case Size.XS:
      return themeSize.units(2);
    case Size.S:
      return themeSize.units(3);
    case Size.M:
      return themeSize.units(4);
    case Size.L:
      return themeSize.units(5);
    case Size.XL:
      return themeSize.units(8);
    case Size.XXL:
      return themeSize.units(10);
    case Size.XXXL:
      return themeSize.units(16);
    default:
      return themeSize.units(4);
  }
};

const getBorderRadius = (themeSize: ThemeSize, size?: Size): string | undefined => {
  switch (size) {
    case Size.XS:
    case Size.S:
    case Size.M:
      return themeSize.units(0.5);
    case Size.L:
    case Size.XL:
    case Size.XXL:
    case Size.XXXL:
      return themeSize.units(1);
    default:
      return themeSize.units(0.5);
  }
};

const getFont = (
  themeSize: ThemeSize,
  size?: Size,
): {
  fontSize: string;
  lineHeight: string;
} => {
  switch (size) {
    case Size.XS:
    case Size.S:
      return {
        fontSize: themeSize.units(1),
        lineHeight: themeSize.units(2),
      };
    case Size.M:
      return {
        fontSize: themeSize.units(1.875),
        lineHeight: themeSize.units(2.5),
      };
    case Size.L:
      return {
        fontSize: themeSize.units(2.25),
        lineHeight: themeSize.units(3),
      };
    case Size.XL:
    case Size.XXL:
    case Size.XXXL:
      return {
        fontSize: themeSize.units(4),
        lineHeight: themeSize.units(3),
      };
    default:
      return {
        fontSize: themeSize.units(1.875),
        lineHeight: themeSize.units(2.5),
      };
  }
};

const getBoxShadow = (themeSize: ThemeSize, themeColor: Color, size?: Size): string => {
  switch (size) {
    case Size.XS:
    case Size.S:
      return `inset 0px 0px 0px 1px ${themeColor.shadow.secondary.default()}`;
    case Size.M:
      return `inset 0px 0px 0px ${themeSize.units(
        0.25,
      )} ${themeColor.shadow.secondary.default()}`;
    case Size.L:
      return `inset 0px 0px 0px ${themeSize.units(
        0.375,
      )} ${themeColor.shadow.secondary.default()}`;
    case Size.XL:
      return `inset 0px 0px 0px ${themeSize.units(
        0.5,
      )} ${themeColor.shadow.secondary.default()}`;
    default:
      return `inset 0px 0px 0px ${themeSize.units(
        0.25,
      )} ${themeColor.shadow.secondary.default()}`;
  }
};

export const TextWrapper = styled.div<TextWrapperStyledProps>(
  ({ $size, theme: { size, transition, font, color } }) => ({
    ...getFont(size, $size),
    fontFamily: font.family,
    borderRadius: getBorderRadius(size, $size),
    transition: transition.standard(),
    fontWeight: 'bold',
    color: color.content.reversed.default(),
    backgroundColor: color.background.tertiary.default(),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    textTransform: 'capitalize',
  }),
);

// WARN: color & background-color are the same color?
export const EditIconWrapper = styled.div(({ theme: { color, transition } }) => ({
  opacity: 0,
  color: color.content.accent.default(),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  transition: transition.standard(),
  height: '100%',
  width: '100%',
  backgroundColor: color.background.reversed.default(),
}));

export const WorkspaceAvatarImage = styled(Image)(
  ({ $src, $size, theme: { color, size } }) => ({
    backgroundSize: $src ? 'cover' : getSize($size, size),
    borderRadius: getBorderRadius(size, $size),
    boxShadow: !$src ? getBoxShadow(size, color, $size) : undefined,
    backgroundColor: color.background.primary.default(),
  }),
);

export const Wrapper = styled.div<WrapperProps>(
  ({ $size, $editable, theme: { size } }) => ({
    position: 'relative',
    minWidth: getSize($size, size),
    minHeight: getSize($size, size),
    width: getSize($size, size),
    height: getSize($size, size),
    borderRadius: getBorderRadius(size, $size),
    cursor: $editable ? 'pointer' : undefined,
    overflow: 'hidden',

    '&:hover': $editable
      ? {
          [EditIconWrapper]: {
            opacity: 1,
          },
        }
      : undefined,
    [TextWrapper]: {
      position: 'absolute',
    },
    [EditIconWrapper]: {
      position: 'absolute',
    },
  }),
);

export const Content = styled.div(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
}));

export const StyledEditIcon = styled(EditIcon)(({ theme: { color, size } }) => ({
  color: color.content.reversed.default(),
  width: size.units(2),
  height: size.units(2),
}));
